/**
 * @typedef {Object} ParsedURLObject
 * @property {string} protocol
 * @property {string} host
 * @property {string} hostname
 * @property {string} port
 * @property {string} pathname
 * @property {string} query
 * @property {Object} queryObject
 * @property {string} hash
 */

/**
 * Returns an object with parsed parts of the specified URL
 *
 * @param {string} url The URL to parse
 * @returns {ParsedURLObject} The parsed URL object
 */
function parseURL(url) {
	const parser = document.createElement('a');
	const searchObject = {};
	let queries = [];
	let split = '';

	parser.href = url;

	if (parser.search) {
		queries = parser.search.replace(/^\?/, '').split('&');

		for (let i = 0; i < queries.length; i++) {
			split = queries[i].split('=');

			if (split.length === 2) {
				const [key, value] = split;

				searchObject[key] = value;
			}
		}
	}

	return {
		protocol: parser.protocol,
		host: parser.host,
		hostname: parser.hostname,
		port: parser.port,
		pathname: parser.pathname,
		query: parser.search,
		queryObject: searchObject,
		hash: parser.hash
	};
}

/**
 * Reform a URL with the properties of a parsed URL object
 *
 * @param {ParsedURLObject} parts The parsed URL object to refrom the URL from
 * @returns {string} The reformed URL
 */
function reformURL(parts) {
	let url = parts.protocol || '';

	url += '//';
	url += parts.hostname || parts.host || '';

	if ('port' in parts && parts.port) {
		url += ':' + parts.port;
	}

	url += parts.pathname || '';

	if ('queryObject' in parts) {
		const str = [];

		Object.keys(parts.queryObject).forEach((key) => {
			str.push(encodeURIComponent(key) + '=' + encodeURIComponent(parts.queryObject[key]));
		});

		parts.query = '?' + str.join('&');
	}

	if ('query' in parts) {
		url += parts.query;
	}

	return url;
}

/**
 * Normalize a hostname by stripping a leading www. and lowercasing
 *
 * @param {string} hostname
 * @returns {string}
 */
function normalizeHostname(hostname) {
	return (hostname || '').replace(/^www\./i, '').toLowerCase();
}

/**
 * Return the embed URL for a YouTube, Vimeo, or Wistia video
 *
 * @param {string} url
 * @param {Object} [options]
 * @param {boolean} [options.background] Apply muted, looping, chrome-hidden params for banner backgrounds
 * @returns {string}
 */
export function getVideoEmbedURL(url, options = {}) {
	const urlParts = parseURL(url);
	const hostname = normalizeHostname(urlParts.hostname);
	const isYouTube =
		hostname === 'youtube.com' ||
		hostname.indexOf('.youtube.com') !== -1 ||
		hostname === 'youtube-nocookie.com' ||
		hostname.indexOf('.youtube-nocookie.com') !== -1;
	const isYoutuBe = hostname === 'youtu.be';
	const isVimeo = hostname === 'vimeo.com' || hostname.indexOf('.vimeo.com') !== -1;

	if (isYouTube) {
		urlParts.queryObject.rel = 0;
		urlParts.queryObject.showinfo = 0;
		urlParts.queryObject.autoplay = 1;
		urlParts.hostname = hostname.indexOf('youtube-nocookie.com') !== -1
			? 'www.youtube-nocookie.com'
			: 'www.youtube.com';

		if ('v' in urlParts.queryObject) {
			urlParts.pathname = `/embed/${urlParts.queryObject.v}`;

			delete urlParts.queryObject.v;
		} else {
			const pathParts = urlParts.pathname.split('/').filter(Boolean);
			const prefixIndex = pathParts.findIndex((part) =>
				['shorts', 'live', 'embed', 'v'].indexOf(part) !== -1
			);
			const videoId =
				prefixIndex !== -1 ? pathParts[prefixIndex + 1] : pathParts[pathParts.length - 1];

			if (videoId) {
				urlParts.pathname = `/embed/${videoId}`;
			}
		}
	} else if (isYoutuBe) {
		urlParts.queryObject.rel = 0;
		urlParts.queryObject.showinfo = 0;
		urlParts.queryObject.autoplay = 1;

		urlParts.hostname = 'www.youtube.com';
		urlParts.pathname = `/embed/${urlParts.pathname.replace(/^\//, '').split('/')[0]}`;
	} else if (isVimeo) {
		urlParts.queryObject.autoplay = 1;
		urlParts.queryObject.portrait = 0;
		urlParts.queryObject.title = 0;
		urlParts.queryObject.byline = 0;

		if (hostname !== 'player.vimeo.com') {
			const pathParts = urlParts.pathname.replace(/^\//, '').split('/').filter(Boolean);
			const vimeoId = pathParts.find((part) => /^\d+$/.test(part));
			const idIndex = pathParts.indexOf(vimeoId);
			const maybeHash = idIndex !== -1 ? pathParts[idIndex + 1] : '';

			if (vimeoId) {
				urlParts.hostname = 'player.vimeo.com';
				urlParts.pathname = `/video/${vimeoId}`;

				if (maybeHash && /^[a-f0-9]+$/i.test(maybeHash)) {
					urlParts.queryObject.h = maybeHash;
				}
			}
		}
	} else if (urlParts.hostname.indexOf('wistia.com') !== -1) {
		urlParts.queryObject.autoPlay = 1;
		urlParts.queryObject.playerColor = '3852ad';
		urlParts.queryObject.videoFoam = 'true';
		urlParts.queryObject.playbar = 'true';
		urlParts.queryObject.settingsControl = 'true';

		let videoId = '';

		if ('wvideo' in urlParts.queryObject) {
			videoId = urlParts.queryObject.wvideo;

			delete urlParts.queryObject.wvideo;
		} else {
			const pathParts = urlParts.pathname.split('/');

			videoId = pathParts[pathParts.length - 1];
		}

		urlParts.hostname = 'fast.wistia.net';
		urlParts.pathname = `/embed/iframe/${videoId}`;
	}

	if (options.background) {
		if (isYouTube || isYoutuBe) {
			const pathParts = urlParts.pathname.split('/').filter(Boolean);
			const videoId = pathParts[pathParts.length - 1];

			urlParts.queryObject.autoplay = 1;
			urlParts.queryObject.mute = 1;
			urlParts.queryObject.loop = 1;
			urlParts.queryObject.controls = 0;
			urlParts.queryObject.modestbranding = 1;
			urlParts.queryObject.playsinline = 1;
			urlParts.queryObject.rel = 0;
			urlParts.queryObject.fs = 0;
			urlParts.queryObject.iv_load_policy = 3;
			urlParts.queryObject.disablekb = 1;

			delete urlParts.queryObject.list;

			if (videoId) {
				urlParts.queryObject.playlist = videoId;
			}
		} else if (isVimeo) {
			urlParts.queryObject.autoplay = 1;
			urlParts.queryObject.muted = 1;
			urlParts.queryObject.loop = 1;
			urlParts.queryObject.background = 1;
			urlParts.queryObject.quality = '1080p';
		}
	}

	return reformURL(urlParts);
}
